import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useParams } from "react-router-dom";
import AddLicenseAndCertificationModal from "../../CompleteYourProfile/components/AddLicenseAndCertificationModal";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { fetchUserQualificationsByType } from "../../../services/api/user";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import { ModuleType, UserType } from "../../../utils/enum";
import useAuth from "../../../hooks/useAuth";
import { verifyOrUnverifyEngineerEquipment } from "../../../services/api/admin";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const LicenseCertification = ({ licencesId, certificationId }) => {
  const { id: userId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { authRole, getPermissionForModule } = useAuth();
  const { hasWriteAccess: hasEngineerSkillsSetWriteAccess } = getPermissionForModule(
    ModuleType.ENGINEER_SKILLS_SET,
  );

  const {
    data: certifications,
    isPending: isCertificationApiLoading,
    error: isCertificationApiError,
    refetch: refetchUserQualificationsByType,
  } = useQuery({
    queryKey: ["certifications", userId],

    queryFn: async () => {
      const [certificationSubs, licenseSubs] = await Promise.all([
        certificationId ? fetchUserQualificationsByType(userId, certificationId) : [],
        licencesId ? fetchUserQualificationsByType(userId, licencesId) : [],
      ]);

      return [...(certificationSubs?.data || []), ...(licenseSubs?.data || [])];
    },
    enabled: !!licencesId || !!certificationId,
  });

  const {
    isPending: isVerifyOrUnverifyEngineerEquipmentMutationApiLoading,
    error: isVerifyOrUnverifyEngineerEquipmentMutationApiError,
    mutateAsync: verifyOrUnverifyEngineerEquipmentMutation,
  } = useMutation({
    mutationFn: verifyOrUnverifyEngineerEquipment,
  });

  const handleVerifyOrUnverifyQualification = async (payload) => {
    try {
      const updatedCertifications = await verifyOrUnverifyEngineerEquipmentMutation(payload);
      queryClient.setQueryData(["certifications", userId], (oldData) => {
        if (!oldData) return oldData;

        return oldData.map((item) =>
          item.id === payload.userQualificationId
            ? { ...item, isVerified: payload.isVerified }
            : item,
        );
      });
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const columns = [
    {
      field: "qualificationSubType",
      headerName: "License / Certification",
      flex: 1,
      renderCell: (params) => (
        <a href={params?.row?.url} target="_blank">
          <p className="text-primary cursor-pointer">{params?.value}</p>
        </a>
      ),
    },
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      renderCell: (params) => <p>{params.value}</p>,
    },
    {
      field: "startDate",
      headerName: "Issue date",
      flex: 0.5,
      renderCell: (params) => <p>{dayjs(params.value).format("MM/DD/YYYY")}</p>,
    },
    {
      field: "endDate",
      headerName: "Expiry Date",
      flex: 1,
      renderCell: (params) => <p>{dayjs(params.value).format("MM/DD/YYYY")}</p>,
    },

    ...(authRole === UserType.ADMIN || authRole === UserType.CLIENT
      ? [
          {
            field: "isVerified",
            headerName: "Verification",
            flex: 1,
            renderCell: (params) => (
              <i
                className={`${!params.value ? "fa-light" : "fa-solid fa-circle-check text-primary"} fa-circle text-sm cursor-pointer ml-2`}
                onClick={() => {
                  if (
                    (authRole === UserType.ADMIN || hasEngineerSkillsSetWriteAccess) &&
                    !isVerifyOrUnverifyEngineerEquipmentMutationApiLoading
                  ) {
                    handleVerifyOrUnverifyQualification({
                      userQualificationId: params.row.id,
                      isVerified: !params.value,
                    });
                  }
                }}
              ></i>
            ),
          },
        ]
      : []),
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <BaseCard className="py-2 px-8 sm:px-10 rounded-lg" elevation={1}>
      <div className="bg-light -mx-4 px-4 py-2 mt-2 mb-4 rounded-md flex justify-between items-center">
        <h6 className="text-dark font-normal">License & Certification</h6>
        {authRole === UserType.ADMIN && (
          <i
            className="fa-solid fa-circle-plus cursor-pointer text-lg text-secondary"
            style={{ cursor: "pointer" }}
            onClick={() => setIsModalOpen(true)}
          ></i>
        )}
      </div>
      <div style={{ width: "100%" }}>
        <div style={{ minWidth: 1200 }}>
          <BaseDataTableV2
            data={certifications}
            rowHeight={38}
            columns={columns}
            isLoading={isCertificationApiLoading}
            disableColumnSorting
            disableColumnMenu
            isCellEditable={(params) => false}
            hideFooter
          />
        </div>
      </div>

      {isModalOpen && (
        <AddLicenseAndCertificationModal
          open={isModalOpen}
          close={setIsModalOpen}
          refetchUserQualificationsByType={refetchUserQualificationsByType}
          licencesId={licencesId}
          certificationId={certificationId}
          isEdit={false}
          setSelectedUserCertificate={() => {}}
          selectedUserCertificate={null}
          qualificationSubTypeId={null}
          targetUserId={userId}
        />
      )}
    </BaseCard>
  );
};

export default LicenseCertification;
